import { SignIn, SignUp } from '@clerk/clerk-react';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Analytics } from '../../lib/analytics';
import { BaseModal } from '../Modal/BaseModal';

interface AuthModalProps {
  mode: 'sign-in' | 'sign-up';
  isOpen: boolean;
  onClose: () => void;
  redirectUrl?: string;
}

export default function AuthModal({ mode, isOpen, onClose, redirectUrl }: AuthModalProps) {
  const location = useLocation();
  const [currentMode, setCurrentMode] = useState(mode);
  
  // Update mode when prop changes
  useEffect(() => {
    setCurrentMode(mode);
  }, [mode]);
  
  // Track sign-in attempts when modal opens in sign-in mode
  useEffect(() => {
    if (isOpen && currentMode === 'sign-in') {
      Analytics.trackSignInAttempted({
        source: 'modal'
      });
    }
  }, [isOpen, currentMode]);
  
  if (!isOpen) return null;
  
  // Default redirect to current location or home
  const defaultRedirect = redirectUrl || location.pathname || '/';
  
  const handleClose = () => {
    onClose();
    // Reset to original mode when closing
    setCurrentMode(mode);
  };
  
  const toggleMode = () => {
    setCurrentMode(currentMode === 'sign-in' ? 'sign-up' : 'sign-in');
  };
  
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="" // Empty title as the Clerk components have their own titles
      showCloseButton={true}
      size="sm"
      className="overflow-visible"
    >
      {currentMode === 'sign-in' ? (
        <SignIn
          appearance={{
            elements: {
              rootBox: 'w-full',
              card: 'shadow-none border-0',
              footer: 'hidden', // Hide default footer, we'll add custom one
            },
          }}
          routing="hash"
          afterSignInUrl={defaultRedirect}
          redirectUrl={defaultRedirect}
        />
      ) : (
        <SignUp
          appearance={{
            elements: {
              rootBox: 'w-full',
              card: 'shadow-none border-0',
              footer: 'hidden', // Hide default footer, we'll add custom one
            },
          }}
          routing="hash"
          afterSignUpUrl={defaultRedirect}
          redirectUrl={defaultRedirect}
        />
      )}
      
      {/* Custom footer for mode switching */}
      <div className="mt-6 text-center text-sm text-muted-foreground">
        {currentMode === 'sign-in' ? (
          <>
            Don't have an account?{' '}
            <button
              onClick={toggleMode}
              className="font-medium text-sport-accent hover:underline"
            >
              Sign up
            </button>
          </>
        ) : (
          <>
            Already have an account?{' '}
            <button
              onClick={toggleMode}
              className="font-medium text-sport-accent hover:underline"
            >
              Sign in
            </button>
          </>
        )}
      </div>
    </BaseModal>
  );
}